import axios from 'axios';
import { offlineService, PendingTMTicket } from './offline.service';
import { API_CONFIG } from '../config/api.config';

const MAX_TM_RETRIES = 5;

export const syncService = {
  async syncPendingActions() {
    const pendingActions = await offlineService.getPendingActions();
    const token = localStorage.getItem('bfa_token');

    if (!token || pendingActions.length === 0) return;

    const results = [];

    for (const action of pendingActions) {
      try {
        const response = await axios({
          method: action.method,
          url: `${API_CONFIG.BASE_URL}${action.endpoint}`,
          data: action.data,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': action.data instanceof FormData ? 'multipart/form-data' : 'application/json'
          }
        });

        if (response.status >= 200 && response.status < 300) {
          await offlineService.clearPendingAction(action.id!);
          results.push({ success: true, action });
        }
      } catch (error) {
        console.error('Failed to sync action:', error);
        results.push({ success: false, action, error });
      }
    }

    return results;
  },

  // Sync pending T&M tickets
  async syncPendingTMTickets(): Promise<{ success: number; failed: number; remaining: number }> {
    const pendingTickets = await offlineService.getPendingTMTickets();
    const token = localStorage.getItem('bfa_token');

    if (!token || pendingTickets.length === 0) {
      return { success: 0, failed: 0, remaining: 0 };
    }

    let successCount = 0;
    let failedCount = 0;

    for (const ticket of pendingTickets) {
      // Skip if too many retries
      if (ticket.retry_count >= MAX_TM_RETRIES) {
        console.warn(`T&M ticket ${ticket.id} exceeded max retries, skipping`);
        failedCount++;
        continue;
      }

      try {
        const response = await axios.post(
          `${API_CONFIG.BASE_URL}/api/tm-tickets/form`,
          {
            job_id: ticket.job_id,
            employee_id: ticket.employee_id,
            ...ticket.ticket_data
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            timeout: 60000 // 60 second timeout for large payloads
          }
        );

        if (response.data?.success) {
          await offlineService.clearPendingTMTicket(ticket.id!);
          successCount++;
          console.log(`Successfully synced T&M ticket ${ticket.id} for job ${ticket.job_name}`);
        } else {
          const errorMsg = response.data?.error || 'Unknown error';
          await offlineService.updateTMTicketRetry(ticket.id!, errorMsg);
          failedCount++;
        }
      } catch (error: any) {
        const errorMsg = error.message || 'Network error';
        console.error(`Failed to sync T&M ticket ${ticket.id}:`, errorMsg);
        await offlineService.updateTMTicketRetry(ticket.id!, errorMsg);
        failedCount++;
      }
    }

    const remaining = await offlineService.getPendingTMTicketCount();
    return { success: successCount, failed: failedCount, remaining };
  },

  startBackgroundSync() {
    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      navigator.serviceWorker.ready.then(registration => {
        return (registration as any).sync.register('bfa-sync');
      });
    }
  },

  // Start periodic sync for T&M tickets (call on app init)
  startTMTicketSync() {
    // Initial sync on startup
    this.syncPendingTMTickets().then(result => {
      if (result.success > 0) {
        console.log(`Synced ${result.success} pending T&M tickets`);
      }
    });

    // Sync when online status changes
    window.addEventListener('online', () => {
      console.log('Network restored, syncing T&M tickets...');
      this.syncPendingTMTickets();
    });

    // Periodic sync every 2 minutes when online
    setInterval(() => {
      if (navigator.onLine) {
        this.syncPendingTMTickets();
      }
    }, 120000);
  }
};
